import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import { StatusBar, Style } from '@capacitor/status-bar';
import { selectDomain } from '../store/selectors/auth.selectors';
import { Domain } from './domain.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ConfigService } from './config.service';
import { Insets } from '../plugins/insets.plugin';

@Injectable({ providedIn: 'root' })
export class StatusBarService implements OnDestroy {
  private initialized = false;
  private domainSub?: Subscription;

  constructor(
    private store: Store,
    private platform: Platform,
    private configService: ConfigService
  ) {}

  init(): void {
    if (this.initialized) return;
    if (!Capacitor.isNativePlatform()) {
      this.initialized = true;
      return;
    }
    this.platform.ready().then(() => {
  StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});

      if (this.platform.is('android')) {
        Insets.getStatusBarHeight()
          .then(({ heightPx }) => {
            document.documentElement.style.setProperty('--status-bar-height', `${heightPx}px`);
            document.body.classList.add('android-insets');
          })
          .catch(() => {});
      }

      const defaultColor = this.configService.apiConfig?.app?.ui_color_bg;
      if (defaultColor) {
        this.setColor(defaultColor);
      }

      this.domainSub = this.store
        .select(selectDomain)
        .pipe(filter((d: Domain | null): d is Domain => !!d))
        .subscribe((domain) => {
          if (domain?.color) {
            this.setColor(domain.color);
          }
        });
      this.initialized = true;
    });
  }

  ngOnDestroy(): void {
    this.domainSub?.unsubscribe();
  }

  private setColor(color: string) {
    if (!Capacitor.isNativePlatform()) return;

    StatusBar.setBackgroundColor({ color }).catch(() => {});
    const style = this.getStyleForBackground(color);
    StatusBar.setStyle({ style }).catch(() => {});
  }

  public getStyleForBackground(color: string): Style {
    const rgb = this.parseHexOrRgb(color);
    if (!rgb) return Style.Dark;
    const [r, g, b] = rgb;
    const brightness = 0.299 * r + 0.587 * g + 0.114 * b; // YIQ
    return brightness >= 186 ? Style.Light : Style.Dark;
  }

  private parseHexOrRgb(color: string): [number, number, number] | null {
    if (!color) return null;
    const v = color.trim().toLowerCase();
    if (v.startsWith('#')) {
      let hex = v.slice(1);
      if (hex.length === 3) {
        hex = hex.split('').map((c) => c + c).join('');
      }
      if (hex.length === 6 || hex.length === 8) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        if ([r, g, b].some((n) => Number.isNaN(n))) return null;
        return [r, g, b];
      }
      return null;
    }
    const m = v.match(/rgba?\(([^)]+)\)/);
    if (m) {
      const parts = m[1].split(',').map((p) => parseFloat(p.trim())).slice(0, 3);
      if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
        return [parts[0], parts[1], parts[2]];
      }
    }
    return null;
  }
}
